import {
  IRequisitionActions,
  IRequisitionApproverActionPayload,
  IRequisitionBuyerActionPayload,
  IRequisitionExportLinesActionPayload,
  IRequisitionPONumberActionPayload,
  IRequisitionFetchActionPayload,
  IRequisitionStateActionPayload,
  RequisitionContext,
  IRequisitionCommentUpdateActionPayload,
  IRequisitionCommentFetchActionPayload,
  IRequisitionAttachmentActionPayload,
  IRequisitionOwnerActionPayload,
  IRequisitionOwnerMultipleUpdateActionPayload,
  IRequisitionCommentAttachmentAddActionPayload,
  IRequisitionCommentAttachmentRemoveActionPayload,
  IRequisitionLineUpdateActionPayload
} from './actions.types';
import { IRequisitionCommentData, IRequisitionData, IUserData } from '@/models';
import {
  IRequisitionPaginatedListData,
  IRequisitionExportFilterParams
} from '@/services';
import { ITransition, Transition } from '@/models/transition';

export const actions: IRequisitionActions = {
  fetchRequisitions(
    context: RequisitionContext,
    payload: IRequisitionFetchActionPayload
  ): Promise<void> {
    const promise = this.$services.requisition.fetchRequisitions(payload);
    context.commit('SET_LAST_PROMISE', promise);
    return promise.then((requisitions: IRequisitionPaginatedListData) => {
      if (promise == context.state.lastPromise) {
        context.commit('SET_REQUISITIONS', requisitions.results);
        context.commit('SET_COUNT', requisitions.count);
        context.commit('SET_LAST_PROMISE', null);
      }
    });
  },

  fetchRequisition(
    context: RequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.requisition
      .fetchRequisition(payload)
      .then((requisition: IRequisitionData) => {
        const index = context.state.requisitions.findIndex(
          (v: IRequisitionData) => v.id === requisition.id
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION', requisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_REQUISITION', requisition);
        }
      });
  },

  createRequisition(
    context: RequisitionContext,
    payload: IRequisitionData
  ): Promise<void> {
    return this.$services.requisition
      .createRequisition(payload)
      .then((requisition: IRequisitionData) => {
        context.commit('ADD_REQUISITION', requisition);
        context.commit('SET_LAST_CREATED_REQUISITION_ID', requisition.id!);
        context.commit('INCREMENT_COUNT');
      });
  },

  updateRequisition(
    context: RequisitionContext,
    payload: IRequisitionData
  ): Promise<void> {
    return this.$services.requisition
      .updateRequisition(payload)
      .then((requisition: IRequisitionData) => {
        const index = context.state.requisitions.findIndex(
          (v: IRequisitionData) => v.id === requisition.id
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION', requisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_REQUISITION', requisition);
        }
      });
  },

  deleteRequisition(
    context: RequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.requisition.deleteRequisition(payload).then(() => {
      context.commit('REMOVE_REQUISITION', payload);
      context.commit('DECREMENT_COUNT');
    });
  },

  updateRequisitionLine(
    context: RequisitionContext,
    payload: IRequisitionLineUpdateActionPayload
  ): void {
    return context.commit('UPDATE_REQUISITION_LINE', {
      id: payload.requisitionId,
      line: payload.line
    });
  },

  deleteRequisitionAttachment(
    context: RequisitionContext,
    payload: IRequisitionAttachmentActionPayload
  ): Promise<void> {
    return this.$services.requisition.deleteRequisitionAttachment(
      payload.requisitionId,
      payload.attachmentId
    );
  },

  updateRequisitionState(
    context: RequisitionContext,
    payload: IRequisitionStateActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .updateRequisitionState(
        payload.requisitionId,
        payload.state,
        payload.assignee,
        payload.comment
      )
      .then((requisition: IRequisitionData) => {
        const index = context.state.requisitions.findIndex(
          (v: IRequisitionData) => v.id === requisition.id
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION', requisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_REQUISITION', requisition);
        }
      });
  },

  fetchRequisitionApprovers(
    context: RequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.requisition
      .fetchRequisitionApprovers(payload)
      .then((approvers: IUserData[]) => {
        context.commit('SET_REQUISITION_APPROVERS', approvers);
      });
  },

  fetchRequisitionBuyers(
    context: RequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.requisition
      .fetchRequisitionBuyers(payload)
      .then((buyers: IUserData[]) => {
        context.commit('SET_REQUISITION_BUYERS', buyers);
      });
  },

  assignRequisitionBuyer(
    context: RequisitionContext,
    payload: IRequisitionBuyerActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .assignRequisitionBuyer(payload.requisitionId, payload.buyer)
      .then((requisition: IRequisitionData) => {
        const index = context.state.requisitions.findIndex(
          (v: IRequisitionData) => v.id === requisition.id
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION', requisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_REQUISITION', requisition);
        }
      });
  },

  updateRequisitionOwner(
    context: RequisitionContext,
    payload: IRequisitionOwnerActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .updateRequisitionOwner(payload.requisitionId, payload.owner)
      .then((requisition: IRequisitionData) => {
        const index = context.state.requisitions.findIndex(
          (v: IRequisitionData) => v.id === requisition.id
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION', requisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_REQUISITION', requisition);
        }
      });
  },

  assignRequisitionPONumber(
    context: RequisitionContext,
    payload: IRequisitionPONumberActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .assignRequisitionPONumber(payload.requisitionId, payload.po_number)
      .then((requisition: IRequisitionData) => {
        const index = context.state.requisitions.findIndex(
          (v: IRequisitionData) => v.id === requisition.id
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION', requisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_REQUISITION', requisition);
        }
      });
  },

  assignRequisitionApprover(
    context: RequisitionContext,
    payload: IRequisitionApproverActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .assignRequisitionApprover(payload.requisitionId, payload.approver)
      .then((requisition: IRequisitionData) => {
        const index = context.state.requisitions.findIndex(
          (v: IRequisitionData) => v.id === requisition.id
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION', requisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_REQUISITION', requisition);
        }
      });
  },

  exportRequisitionLines(
    _context: RequisitionContext,
    payload: IRequisitionExportLinesActionPayload
  ): Promise<void> {
    return this.$services.requisition.exportRequisitionLines(
      payload.requisitionId,
      payload.purchaseOrder
    );
  },

  exportRequisitionHeader(
    _context: RequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.requisition.exportRequisitionHeader(payload);
  },

  fetchRequisitionActivities(
    context: RequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.requisition
      .fetchRequisitionActivities(payload)
      .then((activities: Transition[]) => {
        context.commit('SET_REQUISITION_ACTIVITIES', activities);
      });
  },

  fetchRequisitionComments(
    context: RequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.requisition
      .fetchRequisitionComments(payload)
      .then((comments: ITransition[]) => {
        context.commit('SET_REQUISITION_COMMENTS', comments);
      });
  },
  fetchRequisitionComment(
    context: RequisitionContext,
    payload: IRequisitionCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .fetchRequisitionComment(payload.requisitionId, payload.commentId)
      .then(comment => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION_COMMENT', comment);
        } else {
          context.commit('UPDATE_REQUISITION_COMMENT', comment);
        }
      });
  },
  createRequisitionComment(
    context: RequisitionContext,
    payload: IRequisitionCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .createRequisitionComment(payload.requisitionId, payload.comment)
      .then(comment => {
        context.commit('SET_LAST_CREATED_REQUISITION_COMMENT_ID', comment.id);
        context.commit('ADD_REQUISITION_COMMENT', comment);
      });
  },
  updateRequisitionComment(
    context: RequisitionContext,
    payload: IRequisitionCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .updateRequisitionComment(payload.requisitionId, payload.comment)
      .then(comment => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION_COMMENT', comment);
        } else {
          context.commit('UPDATE_REQUISITION_COMMENT', comment);
        }
      });
  },
  deleteRequisitionComment(
    context: RequisitionContext,
    payload: IRequisitionCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .deleteRequisitionComment(payload.requisitionId, payload.commentId)
      .then(() => {
        context.commit('REMOVE_REQUISITION_COMMENT', payload.commentId);
      });
  },

  addRequisitionCommentAttachments(
    context: RequisitionContext,
    payload: IRequisitionCommentAttachmentAddActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .addRequisitionCommentAttachments(
        payload.requisitionId,
        payload.commentId,
        payload.attachments
      )
      .then((comment: IRequisitionCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );
        if (index === -1) {
          context.commit('ADD_REQUISITION_COMMENT', comment);
        } else {
          context.commit('UPDATE_REQUISITION_COMMENT', comment);
        }
      });
  },

  removeRequisitionCommentAttachment(
    context: RequisitionContext,
    payload: IRequisitionCommentAttachmentRemoveActionPayload
  ): Promise<void> {
    return this.$services.requisition
      .removeRequisitionCommentAttachment(
        payload.requisitionId,
        payload.commentId,
        payload.attachmentId
      )
      .then(() => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );

        if (index !== -1) {
          const comment = context.state.comments[index];

          const attachmentIndex = comment.attachments!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          comment.attachments!.splice(attachmentIndex, 1);

          context.commit('UPDATE_REQUISITION_COMMENT', comment);
        }
      });
  },

  //export
  exportRequisitionPdf(
    _context: RequisitionContext,
    payload: number
  ): Promise<Blob> {
    return this.$services.requisition.exportRequisitionPdf(payload);
  },

  exportRequisitions(
    _context: RequisitionContext,
    payload?: IRequisitionExportFilterParams
  ): Promise<Blob> {
    return this.$services.requisition.exportRequisitions(payload);
  },

  updateMultipleRequisitionOwner(
    _context: RequisitionContext,
    payload: IRequisitionOwnerMultipleUpdateActionPayload
  ): Promise<void> {
    return this.$services.requisition.updateMultipleRequisitionOwner(
      payload.owner,
      payload.requisitions
    );
  },

  // Mentions
  fetchRequisitionCommentsMentionableUsers(
    context: RequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.requisition
      .fetchRequisitionCommentsMentionableUsers(payload)
      .then((mentionableUsers: IUserData[]) => {
        context.commit('SET_MENTIONABLE_USERS', mentionableUsers);
      });
  }
};
